import { create } from 'zustand';

/**
 * Ephemeral UI state only (CLAUDE.md §6, design doc §22.1).
 *
 * Nothing here is remote data, and nothing here is authoritative: every field
 * is a view preference or a temporary selection that may be thrown away on
 * reload without losing work. Remote resources belong to TanStack Query.
 */
export interface UiState {
  /** Which fact's evidence drawer is open, if any. */
  readonly openEvidenceFactId: string | null;
  /** The brand the switcher currently points at, before navigation commits. */
  readonly lastVisitedBrandId: string | null;
  /** Narrow-screen review layout tab (design doc §22.5). */
  readonly reviewTab: 'content' | 'evidence' | 'checks' | 'history';
  readonly theme: 'light' | 'dark';
  setOpenEvidenceFactId: (factId: string | null) => void;
  setLastVisitedBrandId: (brandId: string | null) => void;
  setReviewTab: (tab: UiState['reviewTab']) => void;
  setTheme: (theme: UiState['theme']) => void;
}

/** The state a fresh session starts in. */
const initialUiState = {
  openEvidenceFactId: null,
  lastVisitedBrandId: null,
  reviewTab: 'content',
  theme: 'light',
} as const satisfies Omit<
  UiState,
  'setOpenEvidenceFactId' | 'setLastVisitedBrandId' | 'setReviewTab' | 'setTheme'
>;

export const useUiStore = create<UiState>((set) => ({
  ...initialUiState,
  setOpenEvidenceFactId: (openEvidenceFactId) => set({ openEvidenceFactId }),
  setLastVisitedBrandId: (lastVisitedBrandId) => set({ lastVisitedBrandId }),
  setReviewTab: (reviewTab) => set({ reviewTab }),
  setTheme: (theme) => set({ theme }),
}));

/**
 * Returns the store to its initial state.
 *
 * This store lives at module scope, so Testing Library's `cleanup()` does not
 * touch it: one test leaving the evidence drawer open left it open in the next,
 * whose modal then hid the table. The danger is not the failure — it is a test
 * *passing* on state its own body never set up.
 */
export function resetUiStore(): void {
  useUiStore.setState(initialUiState);
}
